import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPhaseData } from '../../data/programData';
import { getSupersetCount, getWarmupExerciseCount } from '../../data/trainingSessionHelpers';
import { 
  Dumbbell, 
  Play, 
  ChevronRight, 
  CheckCircle,
  Sparkles,
  Clock
} from 'lucide-react';
import { Button } from '../../components/ui';

const SESSION_STORAGE_KEY = 'acl_care_session_progress';

export function TrainingSessionsPage() {
  const { phaseId } = useParams();
  const navigate = useNavigate();
  const { aclCare } = useAuth();
  
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [existingSession, setExistingSession] = useState(null);

  const phaseNum = parseInt(phaseId);
  const phaseData = getPhaseData(phaseNum);
  const phaseKey = `phase_${phaseNum}`;
  const userPhaseData = aclCare?.phases?.[phaseKey];

  // Check for existing session in storage
  const checkForExistingSession = (workoutNum) => {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const today = new Date().toDateString();
        
        if (
          parsed.date === today &&
          parsed.phaseNum === phaseNum &&
          parsed.workoutNum === workoutNum &&
          !parsed.completed
        ) {
          return parsed;
        }
      } catch (e) {
        console.error('Error parsing stored session:', e);
      }
    }
    return null;
  };

  const handleSessionClick = (session) => {
    if (session.isInfo) {
      // Points to Progress - go directly
      navigate(session.path);
      return;
    }

    if (session.isOptional) {
      // Optional workout - go directly for now
      navigate(session.path);
      return;
    }

    // Check for existing session
    const existing = checkForExistingSession(session.workoutNum);
    
    if (existing) {
      setSelectedSession(session);
      setExistingSession(existing);
      setShowResumeModal(true);
    } else {
      // No existing session, go to overview
      navigate(session.path);
    }
  };

  const handleContinueSession = () => {
    // Go directly to player with existing session
    navigate(`/program/phase/${phaseId}/training/session/${selectedSession.workoutNum}/play`);
    setShowResumeModal(false);
  };

  const handleViewOverview = () => {
    // Go to overview (session stays in storage)
    navigate(selectedSession.path);
    setShowResumeModal(false);
  };

  const handleStartNew = () => {
    // Clear existing session and go to overview
    localStorage.removeItem(SESSION_STORAGE_KEY);
    navigate(selectedSession.path);
    setShowResumeModal(false);
  };

  const sessions = [
    {
      id: 'session_1',
      workoutNum: 1,
      title: 'Training Session 1',
      description: `${getSupersetCount(phaseNum, 1)} supersets`,
      completions: userPhaseData?.completions?.session_1 || 0,
      target: phaseData?.targetCompletions?.session_1 || 4,
      icon: Dumbbell,
      color: 'blue',
      path: `/program/phase/${phaseId}/training/session/1`,
    },
    {
      id: 'session_2',
      workoutNum: 2,
      title: 'Training Session 2',
      description: `${getSupersetCount(phaseNum, 2)} supersets`,
      completions: userPhaseData?.completions?.session_2 || 0,
      target: phaseData?.targetCompletions?.session_2 || 4,
      icon: Dumbbell,
      color: 'green',
      path: `/program/phase/${phaseId}/training/session/2`,
    },
    {
      id: 'optional',
      workoutNum: null,
      title: 'Optional Workout',
      description: 'Additional exercises',
      completions: userPhaseData?.completions?.optional || 0,
      target: phaseData?.targetCompletions?.optional || 4,
      icon: Sparkles,
      color: 'purple',
      path: `/program/phase/${phaseId}/optional`,
      isOptional: true,
    },
    {
      id: 'points_to_progress',
      workoutNum: null,
      title: 'Points to Progress',
      description: 'Phase overview videos',
      icon: Play,
      color: 'orange',
      path: `/program/phase/${phaseId}/training/points-to-progress`,
      isInfo: true,
    },
  ];

  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      iconBg: 'bg-blue-100',
      icon: 'text-blue-600',
      progress: 'bg-blue-500',
    },
    green: {
      bg: 'bg-green-50',
      iconBg: 'bg-green-100',
      icon: 'text-green-600',
      progress: 'bg-green-500',
    },
    purple: {
      bg: 'bg-purple-50',
      iconBg: 'bg-purple-100',
      icon: 'text-purple-600',
      progress: 'bg-purple-500',
    },
    orange: {
      bg: 'bg-orange-50',
      iconBg: 'bg-orange-100',
      icon: 'text-orange-600',
      progress: 'bg-orange-500',
    },
  };

  // Calculate progress info for existing session
  const getSessionProgress = () => {
    if (!existingSession) return null;
    
    if (existingSession.phase === 'warmup') {
      return 'In warmup';
    } else if (existingSession.phase === 'supersets') {
      return `Superset ${existingSession.currentSupersetIndex + 1}`;
    }
    return null;
  };

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 lg:pb-8">
      <div className="flex items-center mb-6">
        <Dumbbell className="h-6 w-6 text-primary mr-2" />
        <h1 className="text-2xl font-bold text-gray-900">
          {phaseData?.title} - Training
        </h1>
      </div>

      {/* Warmup Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <p className="text-sm text-gray-600">
          Each session includes a warmup followed by strength supersets.
        </p>
      </div>

      {/* Session Cards */}
      <div className="space-y-4">
        {sessions.map((session) => {
          const Icon = session.icon;
          const colors = colorClasses[session.color];
          const isComplete = session.target && session.completions >= session.target;
          const progress = session.target ? (session.completions / session.target) * 100 : 0;
          
          // Check if this session has an in-progress workout
          const hasInProgress = !session.isInfo && !session.isOptional && checkForExistingSession(session.workoutNum);

          return (
            <button
              key={session.id}
              onClick={() => handleSessionClick(session)}
              className="w-full bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                {/* Icon */}
                <div className={`h-14 w-14 rounded-xl ${colors.iconBg} flex items-center justify-center mr-4 flex-shrink-0 relative`}>
                  <Icon className={`h-7 w-7 ${colors.icon}`} />
                  {hasInProgress && (
                    <div className="absolute -top-1 -right-1 h-4 w-4 bg-yellow-400 rounded-full border-2 border-white" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center">
                      <h3 className="font-semibold text-gray-900">{session.title}</h3>
                      {hasInProgress && (
                        <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                          In Progress
                        </span>
                      )}
                    </div>
                    {isComplete && (
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-500 mb-2">{session.description}</p>

                  {/* Progress bar for sessions with targets */}
                  {session.target && !session.isInfo && (
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Completions</span>
                        <span>{session.completions}/{session.target}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${colors.progress} transition-all`}
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Arrow */}
                <ChevronRight className="h-5 w-5 text-gray-400 ml-2 flex-shrink-0" />
              </div>
            </button>
          );
        })}
      </div>

      {/* Resume Session Modal */}
      {showResumeModal && existingSession && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div 
            className="fixed inset-0 bg-black/50"
            onClick={() => setShowResumeModal(false)}
          />
          
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center mr-4">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Session in Progress</h2>
                  <p className="text-sm text-gray-500">
                    {getSessionProgress()}
                  </p>
                </div>
              </div>

              <p className="text-gray-600 mb-6">
                You have an unfinished {selectedSession?.title} from today. What would you like to do?
              </p>

              <div className="space-y-3">
                <Button
                  onClick={handleContinueSession}
                  fullWidth
                >
                  Continue Where I Left Off
                </Button>
                <Button
                  variant="outline"
                  onClick={handleViewOverview}
                  fullWidth
                >
                  View Session Overview
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleStartNew}
                  fullWidth
                >
                  Start New Session
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}